import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Activity, Flame, TrendingUp } from 'lucide-react';
import { InflammationDashboard } from '@/components/longevity/InflammationDashboard';
import { ProtocolsList } from '@/components/longevity/ProtocolsList';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import WhoopContextCard from '@/components/whoop/WhoopContextCard';
import { PageHeader } from '@/components/PageHeader';

export default function Longevity() {
  return (
    <div className="min-h-screen bg-background">
      <PageHeader title="Longevity & Holistic Health" />
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Longevity & Holistic Health</h1>
          <p className="text-foreground/80 font-light">
            Evidence-based insights and personalised protocols to support your long-term wellbeing
          </p>
          
          {/* WHOOP Context Card - Shows latest biometric data if connected */}
          <div className="mt-4">
            <WhoopContextCard variant="compact" />
          </div>
        </div>

        {/* Correlation Explorer Feature Card */}
        <Card className="mb-8 border-2 border-primary/30 hover:border-primary/60 transition-all shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-primary-foreground" />
              </div>
              <div>
                <CardTitle className="text-2xl">Discover Your Health Correlations</CardTitle>
                <CardDescription className="text-base mt-1">
                  Unlock powerful insights into how your lifestyle impacts your wellbeing
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-6">
              Analyse relationships between diet, sleep, exercise, stress, mental wellness, and WHOOP biometrics. 
              See which factors have the strongest impact on your health and get personalised advice on what to improve next.
            </p>
            <Link to="/vault?tab=correlations">
              <Button size="lg" className="w-full sm:w-auto">
                <TrendingUp className="h-4 w-4 mr-2" />
                Explore Your Data Correlations
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Tabs defaultValue="inflammation" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8 overflow-hidden">
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger value="inflammation" className="flex items-center gap-2">
                  <Flame className="w-4 h-4" />
                  Inflammation
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>Track and manage inflammation levels</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <TabsTrigger value="protocols" className="flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Protocols & Challenges
                </TabsTrigger>
              </TooltipTrigger>
              <TooltipContent>Evidence-based protocols and challenges</TooltipContent>
            </Tooltip>
          </TabsList>

          <TabsContent value="inflammation" className="space-y-6">
            <InflammationDashboard />
          </TabsContent>

          <TabsContent value="protocols" className="space-y-6">
            <ProtocolsList />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
